import { useQuery } from '@tanstack/react-query';
import { SIZE_LABELS, sizeFromHours, type SizeThresholds } from '@rundown/domain';
import type { InternalRequest } from '@rundown/types';
import { formatHours } from '../../lib/format';
import { useCapability } from '../workspace/useCapability';
import { useSizeThresholds } from '../workspace/useWorkspace';
import { fetchIntake } from '../intake/api';
import { intakeQueryKeys } from '../intake/useIntakeState';
import { RequestActions } from './RequestActions';
import { useReviewRequest } from './useRequestMutations';
import { PRIORITY_LABELS, TYPE_LABELS } from './labels';

/**
 * "Needs review" — the banner that makes Noorix's side of a request real.
 *
 * ONE BUTTON, and it is the most consequential control in the product.
 * `type` decides who pays: a Bug is charged against the retainer, a Change
 * consumes the client's included units. The AI proposes that; a named person
 * accepting it on a date is what a later argument turns on, which is why it
 * is recorded as its own decision in the trail rather than as
 * `needs_review: false` among a list of field edits. A client's own wording
 * is a suggestion feeding this decision, never the decision itself
 * (v0.3 §6.1).
 *
 * There used to be a second button, "Ask the client", and it is gone rather
 * than fixed. It cleared `needs_review` without asking anything — its only
 * other effect was to focus the composer — so an interrupted reviewer left a
 * request nobody had asked anything about, marked reviewed, in nobody's
 * queue. And the repaired version was still a button for something that
 * needs none: talking to a client means writing in the thread, which is open
 * on the right-hand side of this very screen. Noorix chats when it needs to;
 * that does not require a control.
 *
 * What the banner shows instead is intake's own record of what it could not
 * pin down, in the client's words — so a reviewer can see whether anything
 * is worth asking before accepting, rather than re-reading the thread to
 * work it out.
 */

export interface ReviewBannerProps {
  readonly workspaceId: string;
  readonly request: InternalRequest;
  /** Open the fields to change what is proposed before accepting. */
  readonly onEditFields?: () => void;
}

/** What intake (or a person) settled on, as the one line a reviewer agrees to. */
function proposalOf(request: InternalRequest, sizeThresholds: SizeThresholds): string | null {
  const classified = request.projectName !== null || request.approxHours > 0;
  if (!classified) return null;
  const size = `${SIZE_LABELS[sizeFromHours(request.approxHours, sizeThresholds)]}${
    request.approxHours > 0 ? ` ~${formatHours(request.approxHours)}` : ''
  }`;
  return [
    TYPE_LABELS[request.type],
    request.projectName ?? 'No project',
    PRIORITY_LABELS[request.priority],
    size,
  ].join(' · ');
}

export function ReviewBanner({ workspaceId, request, onEditFields }: ReviewBannerProps) {
  const canReview = useCapability('request.review');
  const sizeThresholds = useSizeThresholds();
  const review = useReviewRequest(workspaceId, request.id);

  // What the intake conversation never managed to establish, in the client's
  // own words. A plain query rather than `useIntakeState`: by the time a
  // request reaches Review its conversation is terminal, so there is nothing
  // to stream or poll for. Failure is silent — the decision does not depend
  // on it.
  const intake = useQuery({
    queryKey: intakeQueryKeys.intake(workspaceId, request.id),
    queryFn: ({ signal }) => fetchIntake(workspaceId, request.id, signal),
    enabled: canReview && request.needsReview,
    staleTime: 60_000,
    retry: false,
  });
  const missing = intake.data?.missing ?? [];

  // Hidden entirely for anyone who cannot act on it — a banner announcing a
  // decision you are not allowed to make is just a locked door.
  if (!canReview || !request.needsReview) return null;

  const proposal = proposalOf(request, sizeThresholds);

  // One slim strip, not a panel: the decision is "does this read right?", and
  // everything it is about is in the summary line under it. The billing
  // consequence rides on the Accept button's tooltip rather than a paragraph.
  return (
    <div className="mb-1 flex flex-none flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-accent-border bg-accent-tint-soft py-1.5 pr-1.5 pl-3">
      <span className="flex-none fs-12 font-semibold text-accent">Needs review</span>
      <span className="min-w-0 flex-[1_1_12rem] truncate fs-12.5 text-ink-soft" title={proposal ?? undefined}>
        {proposal ?? 'Not classified yet'}
        {missing.length > 0 && (
          <span className="text-muted" title={`Never pinned down: ${missing.join(', ')}`}>
            {' '}
            · {missing.length} open question{missing.length === 1 ? '' : 's'}
          </span>
        )}
      </span>
      <div className="flex flex-none items-center gap-1.5">
        {onEditFields !== undefined && (
          <button
            type="button"
            onClick={onEditFields}
            className="rounded-md px-2 py-1 fs-12 font-medium text-ink-soft hover:bg-accent-tint hover:text-ink"
          >
            Adjust
          </button>
        )}
        <button
          type="button"
          disabled={review.isPending}
          title={`Records that you agree this is a ${TYPE_LABELS[request.type]} with this estimate. That decides how it is billed. To ask the client something first, write in the conversation.`}
          onClick={() => review.mutate('confirmed')}
          className="rounded-md bg-ink px-3 py-1 fs-12 font-semibold whitespace-nowrap text-white disabled:opacity-50"
        >
          {review.isPending ? 'Saving…' : 'Accept'}
        </button>
        <RequestActions workspaceId={workspaceId} request={request} variant="review" />
      </div>
      {review.error !== null && (
        <p role="alert" className="w-full fs-11 text-danger">
          {review.error.message}
        </p>
      )}
    </div>
  );
}
